/**
 * ErrorBoundary that catches "ChunkLoadError" from lazy-loaded routes and
 * auto-recovers by clearing stale caches + hard-reloading. Without this, a
 * user who has an old PWA bundle cached will see a blank page the first time
 * they navigate to a route whose chunk hash changed after redeploy.
 *
 * Ref: this is exactly what caused the "Professionals section blank in
 * production" report — the admin's browser had cached an older React runtime
 * that referenced a chunk hash the new deploy no longer serves.
 */
import React from "react";

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\d]+ failed/i,
  /Loading CSS chunk [\d]+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Expected a JavaScript module/i,
  /Unexpected token '<'/i,
];

// Stale-chunk failures that never reach React: after a redeploy the CDN serves index.html (200, text/html)
// for an old hashed chunk, the <script> throws "Unexpected token '<'" globally and webpack only rejects
// ~120 s later — a long blank screen. Catch them at window level and self-heal once (same 90 s guard).
function healOnce() {
  try {
    const now = Date.now();
    const last = parseInt(sessionStorage.getItem("yorm_chunk_reload_at") || "0", 10);
    if (last && now - last < 90_000) return;
    sessionStorage.setItem("yorm_chunk_reload_at", String(now));
  } catch { /* ignore */ }
  purgeAndReload();
}

export function installChunkErrorHandlers() {
  if (typeof window === "undefined" || window.__yormChunkHandlers) return;
  window.__yormChunkHandlers = true;
  window.addEventListener("error", (e) => {
    const src = (e?.filename || e?.target?.src || "").toString();
    const fromChunk = /\/static\/(js|css)\//.test(src);
    if ((fromChunk && /Unexpected token|SyntaxError/i.test(e?.message || "")) || isChunkError(e?.error)) healOnce();
    else if (e?.target?.tagName === "SCRIPT" && fromChunk) healOnce();
  }, true);
  window.addEventListener("unhandledrejection", (e) => { if (isChunkError(e?.reason)) healOnce(); });
}

function isChunkError(err) {
  if (!err) return false;
  const msg = (err.message || err.toString() || "").toString();
  const name = (err.name || "").toString();
  if (name === "ChunkLoadError") return true;
  return CHUNK_ERROR_PATTERNS.some((rx) => rx.test(msg));
}

async function purgeAndReload() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch { /* silent */ }
  // Bust the HTTP cache with a cache-buster query and reload.
  const u = new URL(window.location.href);
  u.searchParams.set("_r", Date.now().toString(36));
  window.location.replace(u.toString());
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunk: false, autoReloaded: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunk: isChunkError(error) };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Auto-heal chunk-load failures. The Yorm bundle is code-split into ~40
    // lazy chunks; a small percentage of users (especially admins jumping
    // between many pages quickly) see a Cloudflare-side 429 or a stale SW
    // cache pointing at a chunk hash the new deploy no longer serves. The
    // resulting ChunkLoadError bubbles up to this boundary and, historically,
    // rendered a manual "Reload now" screen — which users interpreted as a
    // blank page and gave up.
    //
    // Now: on the FIRST chunk error we purge caches + unregister the SW +
    // hard-reload silently. If that fails and the boundary catches another
    // chunk error within 90 seconds, we surface the manual button so we
    // never loop. The guard uses sessionStorage so a fresh tab still gets
    // one automatic recovery attempt.
    if (!this.state.isChunk) return;
    try {
      const now = Date.now();
      const raw = sessionStorage.getItem("yorm_chunk_reload_at");
      const last = raw ? parseInt(raw, 10) : 0;
      if (!last || now - last > 90_000) {
        sessionStorage.setItem("yorm_chunk_reload_at", String(now));
        // Fire and forget — purgeAndReload navigates away.
        purgeAndReload();
      }
    } catch { /* ignore */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.isChunk) {
      return (
        <div
          role="alert"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            background: "#0d0d1a",
            color: "#fff",
          }}
          data-testid="error-boundary-chunk"
        >
          <div style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>♻️</div>
            <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
              A newer version of Yorm is available
            </h1>
            <p style={{ fontSize: 14, color: "#9ca3af", marginBottom: 20 }}>
              We&apos;re refreshing your app to load the latest version. This only
              happens once after a new release.
            </p>
            <button
              onClick={purgeAndReload}
              data-testid="error-boundary-reload-btn"
              style={{
                background: "#4F988E",
                color: "#fff",
                border: 0,
                padding: "10px 20px",
                borderRadius: 8,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload now
            </button>
          </div>
        </div>
      );
    }

    // Non-chunk error — surface a minimal fallback and a manual reset.
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "system-ui, -apple-system, sans-serif",
          background: "#0d0d1a",
          color: "#fff",
        }}
        data-testid="error-boundary-generic"
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, color: "#9ca3af", marginBottom: 20 }}>
            The page you were viewing crashed. Try refreshing — if it keeps
            happening, please contact Yorm support.
          </p>
          <button
            onClick={purgeAndReload}
            data-testid="error-boundary-reset-btn"
            style={{
              background: "#4F988E",
              color: "#fff",
              border: 0,
              padding: "10px 20px",
              borderRadius: 8,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
